/**
 * Phase 1c — Dedicated authentication screen for the missing-sticker
 * checkout flow.
 *
 * Route: /checkout-auth?pc=<pending_checkout_token>
 *
 * Shows ONLY:
 *   • "Continue with Google" (Emergent OAuth start)
 *   • "Create account with email" (short registration form)
 *   • "Sign in to an existing account"
 * Deliberately hides all order details on this screen (only a "Your order
 * has been saved" affordance). After successful authentication we return
 * the customer to `/order-missing?pc=<token>` where Step 4 (Checkout) is
 * automatically restored.
 */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/api";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import SocialLoginButtons from "@/components/SocialLoginButtons";
import { Loader2, ShieldCheck, ArrowRight, User, LogIn } from "lucide-react";

export default function CheckoutAuth() {
  const loc = useLocation();
  const nav = useNavigate();
  const params = new URLSearchParams(loc.search);
  const pcToken = params.get("pc") || "";
  const { collector, refresh } = useCollectorAuth();
  const [state, setState] = useState({ loading: true, pc: null, error: "", gone: false });
  const [mode, setMode] = useState("choose"); // choose | register | login

  // Load the pending checkout so we can (a) verify it's still valid and
  // (b) prefill the email in the register/login forms. Redirect straight
  // to /order-missing?pc=… if the user is already signed in.
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!pcToken) {
        setState({ loading: false, pc: null, error: "Missing checkout token.", gone: false });
        return;
      }
      try {
        const r = await axios.get(`${API}/pending-checkouts/${pcToken}`);
        if (!alive) return;
        setState({ loading: false, pc: r.data, error: "", gone: false });
        // If already signed in, skip the whole screen.
        if (collector) {
          nav(`/order-missing?pc=${pcToken}`, { replace: true });
        }
      } catch (e) {
        if (!alive) return;
        // Phase 1c hardening — distinguish "gone" (410) from "not found"
        // (404) so returning customers see a clear "your saved order is
        // no longer available" screen with a single Start New Order CTA.
        const status = e?.response?.status || 0;
        const detail = e?.response?.data?.detail || "Could not load your saved order.";
        setState({ loading: false, pc: null, error: detail, gone: status === 410 });
      }
    })();
    return () => { alive = false; };
  }, [pcToken, collector, nav]);

  if (state.loading) {
    return (
      <div className="container-gm max-w-md py-20 flex items-center justify-center" data-testid="checkout-auth-loading">
        <Loader2 className="animate-spin" />
      </div>
    );
  }
  if (state.gone) {
    return (
      <div className="container-gm max-w-lg py-14" data-testid="checkout-auth-gone">
        <div className="rounded-2xl border-4 border-[var(--ink)] bg-white p-6 md:p-8 shadow-[6px_6px_0_var(--panini-yellow)]">
          <div className="flex items-center gap-2 text-[var(--panini-red)]">
            <ShieldCheck size={16} />
            <span className="text-[10px] uppercase tracking-widest font-bold">Saved order unavailable</span>
          </div>
          <h1 className="font-display text-2xl md:text-3xl mt-2">
            Your saved order is no longer available.
          </h1>
          <p className="text-sm text-[var(--ink)] mt-3">
            This can happen if the order has already been submitted or your saved checkout has expired.
          </p>
          <p className="text-sm text-[var(--ink)] mt-2">
            Please return to the Missing Stickers page to start a new order.
          </p>
          <Link
            to="/order-missing"
            className="btn-red text-sm mt-6 inline-flex justify-center"
            data-testid="checkout-auth-start-new-order"
          >
            Start New Order <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="container-gm max-w-md py-14" data-testid="checkout-auth-error">
        <div className="rounded-2xl border-4 border-red-800 bg-red-50 p-6 text-red-900">
          <h1 className="font-display text-2xl">Your saved checkout is unavailable</h1>
          <p className="text-sm mt-2">{state.error}</p>
          <Link to="/order-missing" className="btn-red text-sm mt-5 inline-flex" data-testid="checkout-auth-restart">
            Start a new order <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    );
  }

  const returnTo = `/order-missing?pc=${encodeURIComponent(pcToken)}`;

  return (
    <div className="container-gm max-w-lg py-10 md:py-14" data-testid="checkout-auth-page">
      <div className="rounded-2xl border-4 border-[var(--ink)] bg-white p-6 md:p-8 shadow-[6px_6px_0_var(--panini-yellow)]">
        <div className="flex items-center gap-2 text-[var(--panini-red)]">
          <ShieldCheck size={16} />
          <span className="text-[10px] uppercase tracking-widest font-bold">Your sticker order has been saved</span>
        </div>
        <h1 className="font-display text-2xl md:text-3xl mt-2">
          Create a free account or sign in to continue to checkout.
        </h1>
        <p className="text-sm text-[var(--smoke)] mt-2">
          We&apos;ll bring you straight back to Step 4 — Checkout with your list intact.
        </p>

        {mode === "choose" && (
          <div className="mt-6 space-y-4" data-testid="checkout-auth-choose">
            <SocialLoginButtons mode="login" returnTo={returnTo} showDivider />
            <button
              className="w-full btn-red text-sm justify-center"
              onClick={() => setMode("register")}
              data-testid="checkout-auth-email-register"
            >
              <User size={14} /> Create account with email
            </button>
            <button
              className="w-full btn-ghost text-sm justify-center border-2 border-[var(--ink)]"
              onClick={() => setMode("login")}
              data-testid="checkout-auth-signin"
            >
              <LogIn size={14} /> Sign in to an existing account
            </button>
          </div>
        )}

        {mode === "register" && (
          <RegisterForm
            pcToken={pcToken}
            pc={state.pc}
            onCancel={() => setMode("choose")}
            onSuccess={async () => { await refresh(); nav(returnTo, { replace: true }); }}
          />
        )}

        {mode === "login" && (
          <LoginForm
            pcToken={pcToken}
            pc={state.pc}
            onCancel={() => setMode("choose")}
            onSuccess={async () => { await refresh(); nav(returnTo, { replace: true }); }}
          />
        )}
      </div>
    </div>
  );
}

function RegisterForm({ pcToken, pc, onCancel, onSuccess }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState(pc?.customer?.email || "");
  const [phone, setPhone] = useState(pc?.customer?.phone || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) return toast.error("Enter both first and last name.");
    if (password.length < 10) return toast.error("Password must be at least 10 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      await axios.post(`${API}/collector-auth/checkout-register`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        password,
        confirm_password: confirm,
      }, { withCredentials: true });
      // Sync the customer name/phone into the pending checkout so Step 4
      // pre-fills without another paste.
      try {
        await axios.patch(`${API}/pending-checkouts/${pcToken}`, {
          customer: {
            name: `${firstName.trim()} ${lastName.trim()}`,
            email: email.trim(),
            phone: phone.trim(),
          },
        });
      } catch (_e) { /* non-fatal */ }
      toast.success("Account created. Back to checkout…");
      onSuccess();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not create your account.");
    } finally { setBusy(false); }
  };

  return (
    <form className="mt-6 space-y-3" onSubmit={submit} data-testid="checkout-auth-register-form">
      <div className="grid grid-cols-2 gap-3">
        <input className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm" placeholder="First name"
               value={firstName} onChange={(e) => setFirstName(e.target.value)} required
               data-testid="reg-first-name" />
        <input className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm" placeholder="Last name"
               value={lastName} onChange={(e) => setLastName(e.target.value)} required
               data-testid="reg-last-name" />
      </div>
      <input type="email" className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm" placeholder="Email"
             value={email} onChange={(e) => setEmail(e.target.value)} required
             data-testid="reg-email" />
      <input type="tel" className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm" placeholder="Phone"
             value={phone} onChange={(e) => setPhone(e.target.value)} required
             data-testid="reg-phone" />
      <input type="password" className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm" placeholder="Password (10+ characters)"
             value={password} onChange={(e) => setPassword(e.target.value)} required minLength={10}
             data-testid="reg-password" />
      <input type="password" className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm" placeholder="Confirm password"
             value={confirm} onChange={(e) => setConfirm(e.target.value)} required
             data-testid="reg-confirm" />
      <div className="flex items-center gap-2 pt-1">
        <button type="button" className="btn-ghost text-sm" onClick={onCancel} data-testid="reg-cancel">Back</button>
        <button type="submit" className="btn-red text-sm flex-1 justify-center" disabled={busy} data-testid="reg-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <User size={14} />} Create account & continue
        </button>
      </div>
    </form>
  );
}

function LoginForm({ pcToken, pc, onCancel, onSuccess }) {
  const [email, setEmail] = useState(pc?.customer?.email || "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/collector-auth/login`,
        { email: email.trim(), password },
        { withCredentials: true });
      toast.success("Welcome back. Back to checkout…");
      onSuccess();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Login failed.");
    } finally { setBusy(false); }
  };

  return (
    <form className="mt-6 space-y-3" onSubmit={submit} data-testid="checkout-auth-login-form">
      <input type="email" className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm" placeholder="Email"
             value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
      <input type="password" className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm" placeholder="Password"
             value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
      <div className="flex items-center gap-2 pt-1">
        <button type="button" className="btn-ghost text-sm" onClick={onCancel} data-testid="login-cancel">Back</button>
        <button type="submit" className="btn-red text-sm flex-1 justify-center" disabled={busy} data-testid="login-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <LogIn size={14} />} Sign in & continue
        </button>
      </div>
      <Link to="/forgot-password" className="block text-center text-xs text-[var(--smoke)] underline pt-1" data-testid="login-forgot">
        Forgot password?
      </Link>
    </form>
  );
}
